import React, { useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import { downloadOutput } from "../../actions/files/downloadOutput";
import GlassPanel from "../../views/scribe/GlassPanel";
import AnimatedStreamingText from "../common/AnimatedStreamingText";

interface QaResultPanelProps {
  responseText: string;
  isStreamingResponse?: boolean;
  downloadTarget: string;
  onSendToDocChat?: () => void;
  onClear: () => void;
}

const copyToClipboard = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "true");
  textArea.style.position = "absolute";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
};

const buildMailtoLink = (text: string) => {
  let subject = "Q&A Assist Response";
  let body = text;
  const lines = text.trim().split("\n");
  for (const line of lines) {
    if (line.trim().toLowerCase().startsWith("subject:")) {
      subject = line.trim().slice(8).trim() || subject;
      body = lines.slice(1).join("\n").trim();
      break;
    }
  }
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const QaResultPanel: React.FC<QaResultPanelProps> = ({
  responseText,
  isStreamingResponse = false,
  downloadTarget,
  onSendToDocChat,
  onClear,
}) => {
  const [copyLabel, setCopyLabel] = useState<"idle" | "copied">("idle");
  const [downloadMessage, setDownloadMessage] = useState("");

  const wordCount = useMemo(() => {
    const words = responseText.trim().split(/\s+/u);
    return responseText.trim() ? words.length : 0;
  }, [responseText]);

  const mailtoLink = useMemo(() => buildMailtoLink(responseText), [responseText]);

  const handleCopy = async () => {
    try {
      await copyToClipboard(responseText);
      setCopyLabel("copied");
      window.setTimeout(() => setCopyLabel("idle"), 1800);
    } catch {
      /* silent */
    }
  };

  const handleDownload = async () => {
    if (!downloadTarget) return;
    try {
      await downloadOutput(downloadTarget);
      setDownloadMessage("Downloaded");
      window.setTimeout(() => setDownloadMessage(""), 1800);
    } catch {
      setDownloadMessage("Download failed");
      window.setTimeout(() => setDownloadMessage(""), 1800);
    }
  };

  if (!responseText.trim()) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            flex: 1,
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(44,192,183,0.35), transparent)",
          }}
        />
        <Typography
          sx={{
            fontFamily: "'Syne', sans-serif",
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--teal)",
            whiteSpace: "nowrap",
          }}
        >
          AI Response
        </Typography>
        <Box
          sx={{
            flex: 1,
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(44,192,183,0.35), transparent)",
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <Chip
          label={`${isStreamingResponse ? "Generating" : "AI Generated"} \u00B7 ${wordCount.toLocaleString()} words`}
          size="small"
          sx={{
            bgcolor: isStreamingResponse ? "rgba(44,192,183,0.12)" : "rgba(245,166,35,0.12)",
            border: isStreamingResponse ? "1px solid rgba(44,192,183,0.2)" : "1px solid rgba(245,166,35,0.2)",
            color: isStreamingResponse ? "rgba(44,192,183,0.92)" : "rgba(245,166,35,0.92)",
            fontWeight: 600,
            fontSize: "0.68rem",
            "& .MuiChip-label": { px: 1 },
          }}
        />
        {isStreamingResponse && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1,
              py: 0.45,
              borderRadius: "999px",
              border: "1px solid rgba(44, 192, 183, 0.2)",
              background: "rgba(44, 192, 183, 0.08)",
              color: "rgba(153, 246, 228, 0.96)",
            }}
          >
            <CircularProgress size={12} thickness={6} sx={{ color: "var(--teal)" }} />
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
              Still generating
            </Typography>
          </Box>
        )}
      </Box>

      <GlassPanel>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontSize: "0.8rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--teal)",
            }}
          >
            Generated Response
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => void handleCopy()}
              startIcon={copyLabel === "copied" ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
              sx={{ fontSize: "0.72rem", minWidth: 0 }}
            >
              {copyLabel === "copied" ? "Copied" : "Copy"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              component="a"
              href={mailtoLink}
              startIcon={<EmailOutlinedIcon />}
              sx={{ fontSize: "0.72rem", minWidth: 0 }}
            >
              Email
            </Button>
          </Box>
        </Box>

        <AnimatedStreamingText
          text={responseText}
          isStreaming={isStreamingResponse}
          minHeight={120}
          maxHeight={420}
          fontSize="0.86rem"
          fontFamily="'DM Sans', sans-serif"
          lineHeight={1.75}
        />
      </GlassPanel>

      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        {downloadTarget && (
          <Button
            variant="contained"
            size="large"
            onClick={() => void handleDownload()}
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{ flex: 1 }}
          >
            {downloadMessage || "Download Word"}
          </Button>
        )}
        {onSendToDocChat && (
          <Button
            variant="contained"
            size="large"
            onClick={onSendToDocChat}
            startIcon={<ChatBubbleOutlineRoundedIcon />}
            sx={{ flex: 1 }}
          >
            Send to Doc Chat
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Button
          variant="text"
          size="small"
          onClick={onClear}
          startIcon={<AutorenewRoundedIcon />}
          sx={{ color: "var(--t3)", fontSize: "0.78rem" }}
        >
          New Q&amp;A
        </Button>
      </Box>
    </Box>
  );
};

export default QaResultPanel;
